import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_BASE } from '../api';

export function usePlayer(token) {
  const [songs, setSongs] = useState([]);
  const [currentSongIndex, setCurrentSongIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(new Audio());
  const songsRef = useRef(songs);
  const currentSongIndexRef = useRef(currentSongIndex);
  const isPlayingRef = useRef(isPlaying);
  const tokenRef = useRef(token);

  useEffect(() => { songsRef.current = songs; }, [songs]);
  useEffect(() => { currentSongIndexRef.current = currentSongIndex; }, [currentSongIndex]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const savePlayback = useCallback(async (songFileName, position) => {
    const t = tokenRef.current;
    if (!t) return;
    try {
      await fetch(`${API_BASE}/api/playback/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
        body: JSON.stringify({ songFileName, position })
      });
    } catch (err) {
      console.error('Error saving playback:', err);
    }
  }, []);

  const playSong = useCallback((index) => {
    const s = songsRef.current;
    const ci = currentSongIndexRef.current;
    const audio = audioRef.current;

    if (ci === index) {
      if (isPlayingRef.current) {
        savePlayback(s[index].fileName, audio.currentTime);
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
    } else {
      if (ci !== -1) {
        savePlayback(s[ci].fileName, audio.currentTime);
      }
      setCurrentSongIndex(index);
      audio.src = `${API_BASE}${s[index].url}`;
      audio.play();
      setIsPlaying(true);
    }
  }, [savePlayback]);

  const nextSong = useCallback(() => {
    const s = songsRef.current;
    if (s.length === 0) return;
    const ci = currentSongIndexRef.current;
    playSong((ci + 1) % s.length);
  }, [playSong]);

  const prevSong = useCallback(() => {
    const s = songsRef.current;
    if (s.length === 0) return;
    const ci = currentSongIndexRef.current;
    playSong((ci - 1 + s.length) % s.length);
  }, [playSong]);

  const handleSeek = useCallback((e) => {
    const seekTime = (e.target.value / 100) * audioRef.current.duration;
    audioRef.current.currentTime = seekTime;
    setProgress(e.target.value);
  }, []);

  const refreshSongs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/songs`);
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        throw new Error(data?.error || `Oväntat svar från servern (HTTP ${res.status})`);
      }
      setSongs(data);
    } catch (err) {
      console.error('Error fetching songs:', err);
    }
  }, []);

  const restorePlayback = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) return;
    try {
      const res = await fetch(`${API_BASE}/api/playback/restore`, {
        headers: { 'Authorization': `Bearer ${t}` }
      });
      if (!res.ok) return;
      const { song_filename, position } = await res.json();

      const r = await fetch(`${API_BASE}/api/songs`);
      const currentSongs = await r.json();
      if (!r.ok || !Array.isArray(currentSongs)) {
        throw new Error('Oväntat svar från servern');
      }
      setSongs(currentSongs);

      const index = currentSongs.findIndex(s => s.fileName === song_filename);
      if (index !== -1) {
        setCurrentSongIndex(index);
        audioRef.current.src = `${API_BASE}${currentSongs[index].url}`;
        audioRef.current.currentTime = position;
      }
    } catch (err) {
      console.error('Error restoring playback:', err);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    setProgress((audio.currentTime / audio.duration) * 100 || 0);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    setDuration(audioRef.current.duration);
  }, []);

  const handleEnded = useCallback(() => {
    const s = songsRef.current;
    if (s.length === 0) return;
    const ci = currentSongIndexRef.current;
    playSong((ci + 1) % s.length);
  }, [playSong]);

  useEffect(() => {
    refreshSongs();
    restorePlayback();
    const audio = audioRef.current;
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  const sortedSongs = useMemo(
    () => [...songs].sort((a, b) => a.title.localeCompare(b.title, 'sv')),
    [songs]
  );

  const currentSong = currentSongIndex >= 0 ? songs[currentSongIndex] : undefined;

  return {
    songs, sortedSongs, currentSong, currentSongIndex,
    isPlaying, volume, progress, duration, audioRef,
    playSong, nextSong, prevSong, handleSeek,
    refreshSongs, setVolume
  };
}
